import { useRef, type MutableRefObject } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import { useEventListener } from '@fiverr-private/hooks';
import { PACKAGE_CLASSNAME } from '@fiverr-private/header';
import { useScrollCallback } from '../../../../../shared/hooks/useScrollCallback/useScrollCallback';
import { toggleCategoriesMenu } from '../../../../../shared/utils/categoriesMenuToggler';
import { ANCHORS_BAR_SELECTOR } from '../../AnchorsBar/constants';

/** `onShown` fires every time scrolling reveals the categories menu after it was hidden */
export const useCategoriesToggle = (triggerRef: MutableRefObject<HTMLElement | null>, onShown?: () => void) => {
    const { isMobile } = getContext();
    const isVisibleRef = useRef(false);
    const anchorsBarRef = useRef<HTMLElement | null | undefined>(undefined);
    const callback = (_event?: Event, unsubscribe?: () => void) => {
        if (isMobile) {
            return;
        }
        const header = document.querySelector<HTMLElement>(`header.${PACKAGE_CLASSNAME}`);
        if (!header) {
            logger.error({
                message: 'Header element not found. CategoriesMenu will be always rendered as a safe fallback',
            });
            toggleCategoriesMenu(true);
            unsubscribe?.();
            return;
        }
        if (!triggerRef.current) {
            logger.error({
                message: 'Scroll trigger element not found. CategoriesMenu will be always rendered as a safe fallback',
            });
            toggleCategoriesMenu(true);
            unsubscribe?.();
            return;
        }
        const triggerRect = triggerRef.current.getBoundingClientRect();
        if (anchorsBarRef.current === undefined) {
            anchorsBarRef.current = document.querySelector<HTMLElement>(ANCHORS_BAR_SELECTOR);
        }
        const anchorsBarRect = anchorsBarRef.current?.getBoundingClientRect();
        // The bar is display:none below md, so fall back to the header.
        const boundary = anchorsBarRect?.height ? anchorsBarRect : header.getBoundingClientRect();
        const isShown = triggerRect.bottom < boundary.bottom;

        toggleCategoriesMenu(isShown);

        if (isShown && !isVisibleRef.current) {
            onShown?.();
        }

        isVisibleRef.current = isShown;
    };
    useScrollCallback(!isMobile ? callback : undefined);
    useEventListener('load', callback, typeof window !== 'undefined' ? window : null);
};
